import { CallbackHandler } from '../chat/callbacks/handler';
import ConnectionState from '../chat/enums/ConnectionState';
import { getAuthToken } from '../network/cookie';
import { notifyUnread } from '../chat/utils/UnreadNotifier';

type RpcRequest = {
  type: 'rpc';
  id: string;
  target: string;
  method: string;
  args: any[];
};

type RpcResponse = {
  type: 'rpc:result';
  id: string;
  ok: boolean;
  result?: any;
  error?: string;
};

type CallbackEvent = {
  type: 'callback';
  name: string;
  payload: any;
};

type UnreadEvent = {
  type: 'unread';
  unread: number;
};

type ReadyEvent = { type: 'ready' };

type EnvMessage = {
  type: 'env';
  authToken?: string;
  hostname?: string;
  pathname?: string;
  online?: boolean;
};

type WorkerEvent = RpcResponse | CallbackEvent | UnreadEvent | ReadyEvent;

const hasSharedWorker = () => typeof SharedWorker !== 'undefined';

export default class ChatWorkerClient {
  static shared = new ChatWorkerClient();

  private port: MessagePort | null = null;
  private pending = new Map<
    string,
    {
      resolve: (value: any) => void;
      reject: (reason?: any) => void;
    }
  >();
  private isReady = false;
  private readyResolver?: () => void;
  private readyPromise: Promise<void> = new Promise((resolve) => {
    this.readyResolver = resolve;
  });
  private state: ConnectionState = ConnectionState.NOT_CONNECTED;
  private lastEnvSignature?: string;
  private mode: 'worker' | 'direct' =
    typeof window !== 'undefined' && hasSharedWorker() ? 'worker' : 'direct';

  private constructor() {
    console.log('[chat-worker] mode check', {
      hasWindow: typeof window !== 'undefined',
      hasSharedWorker: hasSharedWorker(),
      mode: this.mode
    });

    if (this.mode === 'worker') {
      const worker = new SharedWorker(new URL('./chatSharedWorker.ts', import.meta.url), {
        type: 'module',
        name: 'myt-chat-shared-worker'
      });
      console.log('[chat-worker] starting shared worker');
      this.port = worker.port;
      this.port.onmessage = (event) => this.onMessage(event.data as WorkerEvent);
      this.port.start();
      this.sendEnv(true);

      window.addEventListener('online', () => this.sendEnv(true));
      window.addEventListener('offline', () => this.sendEnv(true));
    } else {
      console.warn('[chat-worker] falling back to direct mode', {
        hasWindow: typeof window !== 'undefined',
        hasSharedWorker: hasSharedWorker()
      });
    }
  }

  isWorkerMode = () => this.mode === 'worker';

  connectionState = () => this.state;

  private onMessage = (event: WorkerEvent) => {
    if (event.type === 'ready') {
      this.isReady = true;
      this.readyResolver?.();
      console.log('[chat-worker] worker ready');
      return;
    }

    if (event.type === 'rpc:result') {
      const pending = this.pending.get(event.id);
      if (!pending) {
        return;
      }
      this.pending.delete(event.id);
      if (event.ok) {
        pending.resolve(event.result);
      } else {
        pending.reject(event.error);
      }
      return;
    }

    if (event.type === 'unread') {
      notifyUnread(event.unread);
      return;
    }

    if (event.type === 'callback') {
      this.handleCallback(event.name, event.payload);
    }
  };

  private handleCallback = (name: string, payload: any) => {
    switch (name) {
      case 'connectionStateChanged':
        this.state = payload.state as ConnectionState;
        CallbackHandler.shared.callOnConnectionStateChanged(this.state);
        return;
      case 'typingStateChanged':
        CallbackHandler.shared.callOnTypingStateChanged(
          payload.channelId,
          payload.from,
          payload.typing
        );
        return;
      case 'smsChannelData':
        CallbackHandler.shared.callOnSMSChannelData(payload.item);
        return;
      case 'recoData':
        CallbackHandler.shared.callOnRecoData(payload.users, payload.hasMore, payload.type);
        return;
      case 'relayData':
        CallbackHandler.shared.callOnRelayData(payload.items, payload.hasMore);
        return;
      case 'relayItem':
        CallbackHandler.shared.callOnRelayItem(payload.item);
        return;
      case 'presenceReceived':
        CallbackHandler.shared.callOnPresenceReceived(payload.uuid, payload.status);
        return;
      case 'progressUpdated':
        CallbackHandler.shared.callOnProgressUpdated(
          payload.message,
          payload.mediaIdx,
          payload.progress
        );
        return;
      case 'messageAdded':
        CallbackHandler.shared.callOnMessageAdded(payload.message);
        return;
      default:
        return;
    }
  };

  private sendEnv = (force?: boolean) => {
    if (!this.port) {
      return;
    }
    let authToken: string | undefined;
    try {
      authToken = getAuthToken();
    } catch {
      authToken = undefined;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const payload: EnvMessage = {
      type: 'env',
      authToken,
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      online: window.navigator.onLine
    };

    const signature = `${payload.authToken || ''}|${payload.hostname || ''}|${payload.pathname || ''}|${
      payload.online ? '1' : '0'
    }`;

    if (force !== true && signature === this.lastEnvSignature) {
      return;
    }

    this.lastEnvSignature = signature;
    this.port.postMessage(payload);
  };

  call = async (target: string, method: string, args: any[] = []) => {
    if (this.mode === 'direct') {
      throw new Error('ChatWorkerClient in direct mode should not be used for RPC');
    }
    if (!this.port) {
      throw new Error('Chat shared worker not initialized');
    }
    await this.readyPromise;
    this.sendEnv();
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const msg: RpcRequest = { type: 'rpc', id, target, method, args };
    const promise = new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
    });
    this.port.postMessage(msg);
    return promise;
  };
}
